import { ConfigApi, FetchApi, OAuthApi } from '@backstage/core-plugin-api';
import { MaxxApi, MaxxInterimSummary, MaxxWeekSummary } from './MaxxApi';

export interface MaxxApiClientOptions {
  configApi: ConfigApi;
  fetchApi: FetchApi;
  oauthApi: OAuthApi;
}

export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export class MaxxApiClient implements MaxxApi {
  private readonly configApi: ConfigApi;
  private readonly fetchApi: FetchApi;
  private readonly oauthApi: OAuthApi;

  constructor(options: MaxxApiClientOptions) {
    this.configApi = options.configApi;
    this.fetchApi = options.fetchApi;
    this.oauthApi = options.oauthApi;
  }

  async readInterimSummary(): Promise<MaxxInterimSummary> {
    const baseUrl = this.configApi?.getString('home-module-cards.maxxBaseUrl') // e.g. https://www.exxcellent.de/maxx/external
    const maxxApiUrl = baseUrl.endsWith('/') ? `${baseUrl.substring(0, baseUrl.length - 1)  }/api` : baseUrl + '/api';

    // 1. get an oauth accesstoken (the identityApi would give us a useless backstage internal token)
    const accessToken = await this.oauthApi.getAccessToken("openid profile email");

    // 2. log into maxx, to authorize
    const loginUrl = `${maxxApiUrl}/login`;
    const method = 'POST';
    const result1 = await this.fetchApi.fetch(loginUrl, {
      method: method,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    const data = await result1.json();
    const userId = data.userId;
    const accessDate = formatDate(new Date());

    // 3. Fetch the interim summary from maxx api
    const targetUrl = `${maxxApiUrl}/users/${userId}/interim-summary/${accessDate}`;
    const result2 = await this.fetchApi.fetch(targetUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    const data2 = await result2.json();
    if (!result2.ok) {
        throw new Error(`${data.message}`);
    }
    return {
      ...data2,
      userId,
      accessDate
    };
  }

  async readWeekSummary(accessDateString: string): Promise<MaxxWeekSummary> {
    const baseUrl = this.configApi?.getString('home-module-cards.maxxBaseUrl');
    const maxxApiUrl = baseUrl.endsWith('/') ? `${baseUrl.substring(0, baseUrl.length - 1)}/api` : baseUrl + '/api';

    const accessToken = await this.oauthApi.getAccessToken('openid profile email');

    const loginUrl = `${maxxApiUrl}/login`;
    const loginResult = await this.fetchApi.fetch(loginUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    const loginData = await loginResult.json();
    const userId = loginData.userId;

    // Determine Monday of the week containing accessDateString
    const anchor = new Date(accessDateString);
    const dayOfWeek = anchor.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(anchor);
    monday.setDate(anchor.getDate() + diffToMonday);

    const weekDates = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return formatDate(d);
    });

    const fetchDay = async (dateStr: string) => {
      const url = `${maxxApiUrl}/users/${userId}/interim-summary/${dateStr}`;
      const res = await this.fetchApi.fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        return null;
      }
      return res.json();
    };

    const dayResults = await Promise.all(weekDates.map(fetchDay));

    // Use targetWorkingHours from the first non-null result
    const firstValid = dayResults.find(d => d !== null);
    const targetWorkingHours: number = firstValid?.targetWorkingHours ?? 40;
    const dailyTarget = targetWorkingHours / 5;

    const bookings = dayResults.map(d => ({
      hoursBookedOnDay: d?.hoursBookedOnDay ?? 0,
      FLEXTIME: d?.balances?.FLEXTIME ?? 0,
      hoursPercentage: dailyTarget > 0 ? (d?.hoursBookedOnDay ?? 0) / dailyTarget : 0,
    }));

    // accessFLEXTIME: FLEXTIME from the entry matching accessDateString
    const accessIndex = weekDates.indexOf(accessDateString);
    const accessFLEXTIME = accessIndex >= 0 ? bookings[accessIndex].FLEXTIME : 0;

    return {
      bookings,
      userId,
      accessDateString,
      accessFLEXTIME,
    };
  }
}

